import {
  BiSolidBriefcase,
  BiSolidCalendar,
  BiSolidGraduation,
} from "solid-icons/bi";
import { type Component, children, For, type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { PortfolioLinks } from "@/components/shared/portfolio-links";
import { LanguagesTags, SkillsTags } from "@/components/shared/tags";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MatchingProgressBar } from "@/domains/company/active-search/components/MatchingProgressBar";
import { isAvailableNow } from "@/domains/freelance/availability/utils/is-available-now";
import {
  useGetAllProfileEducationV2,
  useGetAllProfileJobExperienceV2,
} from "@/domains/freelance/profile/api";
import type { PublicProfileV2 } from "@/domains/freelance/profile/api/types";
import { formatMonthYearReadable } from "@/libs/date";
import { formatDailyRate } from "@/libs/format";
import { RecommendationsSection } from "./RecommendationsSection";

/**
 * Guasto di **una** sezione, isolato dal resto della pagina.
 *
 * Ingombro deliberatamente minimo: il resto del profilo resta leggibile e
 * scorrevole, e il "Riprova" ricarica solo la query che è caduta. Definito a
 * livello di modulo e non dentro `ProfileInfo`, che lo ricreerebbe a ogni
 * render.
 */
const SectionError: Component<{ onRetry: () => void }> = (props) => {
  const { t } = useI18n();

  return (
    <div class="flex flex-col items-start gap-2 py-2">
      <p class="text-sm text-muted-foreground">
        {t("profile.details.profileInfo.section_error")}
      </p>
      <Button variant="outline" size="sm" onClick={() => props.onRetry()}>
        {t("common.retry")}
      </Button>
    </div>
  );
};

export const ProfileInfo: Component<{
  /**
   * La vista **pubblica**, non la riga intera: questo componente è renderizzato
   * anche per un anonimo, e i campi che il backend non manda più (email,
   * telefono, foto, stato dell'onboarding) devono risultare inesistenti qui,
   * non `undefined` a runtime. Il profilo del proprietario resta assegnabile.
   */
  profile: PublicProfileV2 & { confidence_score?: number };
  /**
   * Slot opzionale ancorato subito sotto la barra del match — oggi il box
   * "Perché questo match" della ricerca attiva. Sta qui e non nel chiamante
   * perché la spiegazione deve restare attaccata al punteggio che spiega.
   */
  matchExplanation?: JSX.Element;
}> = (props) => {
  const profile = () => props.profile;
  const { t } = useI18n();
  // `children` memoizza la risoluzione: lo slot viene creato una volta sola
  // anche se lo leggiamo sia nella condizione sia nel corpo.
  const matchSlot = children(() => props.matchExplanation);
  // Accessor, non `profile().id`: la sheet della ricerca attiva cambia
  // freelancer senza rimontare questo componente, e un id letto una volta
  // congelerebbe le tre query sul primo — esperienze e formazione di una
  // persona sotto il nome di un'altra. Stessa regola di `params.id` nella rotta.
  const profileId = () => profile().id;
  const jobsQuery = useGetAllProfileJobExperienceV2(profileId);
  const educationQuery = useGetAllProfileEducationV2(profileId);

  return (
    <div class="max-w-full p-2 w-full lg:mx-auto border-collapse border-none">
      <div class="flex-1 w-full space-y-6">
        {/* Profile Header */}
        <div class="space-y-4">
          <div class="flex-1 space-y-4">
            <h1 class="text-xl lg:text-2xl font-semibold mb-1">
              {profile()?.full_name}
            </h1>
            <Show when={profile()?.job_title}>
              <p class="text-base  text-gray-700  mb-2">
                {profile()?.job_title}
              </p>
            </Show>

            <div class="flex items-center gap-2 flex-wrap mb-3">
              <Show when={profile().price_per_day}>
                <Badge variant="info" size="sm">
                  {formatDailyRate(profile()?.price_per_day)}
                </Badge>
              </Show>
              {/*
                `available_from` è una data di inizio, non un interruttore:
                mostrare il badge appena il campo esiste dichiarava disponibile
                oggi chi si è dichiarato libero fra due mesi. Stessa regola del
                chip nell'header (`ProfileLayout.tsx`), scritta una volta sola.
              */}
              <Show when={isAvailableNow(profile()?.available_from)}>
                <Badge variant="secondary" size="sm">
                  {t("availability.available")}
                </Badge>
              </Show>
              {/*
                Un badge solo per la modalità di lavoro. Il secondo — «Disponibile
                a venire sul posto» per `onsite` e `hybrid` — ripeteva la parola
                «Disponibile» proprio quando `available_from` è futuro e il badge
                di disponibilità è spento, e per `onsite` diceva due volte lo
                stesso fatto («In sede · Disponibile a venire sul posto»).
              */}
              <Show when={profile()?.work_preference}>
                {(remotePolicy) => (
                  <Badge variant="secondary" size="sm">
                    {t(`common.remote_policy_options.${remotePolicy()}`)}
                  </Badge>
                )}
              </Show>
              <Show when={profile()?.residency}>
                <Badge variant="secondary" size="sm">
                  {profile()?.residency}
                </Badge>
              </Show>
            </div>

            {/* Skills Tags */}
            <Show when={profile()?.skills && profile()!.skills!.length > 0}>
              <div class="space-y-2">
                <p class="text-sm text-gray-600">
                  {t("profile.details.profileInfo.skills")}
                </p>
                <div class="flex items-center gap-2 flex-wrap">
                  <SkillsTags
                    skills={profile()!.skills ?? []}
                    slicedSkills={profile()!.skills ?? []}
                  />
                </div>
              </div>
            </Show>

            {/* Languages */}
            <Show
              when={
                profile()?.languages &&
                Object.keys(profile()!.languages!).length > 0
              }
            >
              <div class="space-y-2">
                <p class="text-sm text-gray-600">
                  {t("profile.details.profileInfo.languages")}
                </p>
                <div class="flex items-center gap-2 flex-wrap">
                  <LanguagesTags languages={profile()!.languages!} />
                </div>
              </div>
            </Show>
          </div>
        </div>
        <Show when={profile().confidence_score || matchSlot()}>
          <div class="space-y-3">
            <Show when={profile().confidence_score}>
              <MatchingProgressBar score={profile().confidence_score!} />
            </Show>
            {matchSlot()}
          </div>
        </Show>

        <hr class="my-4 border-gray-200" />

        {/* About Me Section */}
        <Show when={profile()?.bio}>
          <div class="space-y-2">
            <h2 class="text-base font-medium mb-2">
              {t("profile.details.profileInfo.bio")}
            </h2>
            <div class="space-y-1">
              <p class="text-sm text-gray-700 leading-relaxed">
                {profile()?.bio}
              </p>
            </div>
          </div>
          <hr class="my-4 border-gray-200" />
        </Show>

        {/* Portfolio & shared links — website, portfolio and every social
            (LinkedIn, GitHub, X) live here in one card. */}
        <PortfolioLinks
          portfolio={profile()?.portfolio}
          website={profile()?.website}
          linkedin={profile()?.social_media?.linkedin}
          github={profile()?.social_media?.github}
          twitter={profile()?.social_media?.twitter}
        />

        {/*
          Recommendations — nascosta del tutto quando vuota in questa spalla in
          sola lettura, e **sempre** nella variante pubblica: `ProfileInfo` è la
          scheda che il proprietario usa come anteprima di ciò che sta per
          condividere, quindi deve mostrargli esattamente quello che vedranno
          gli altri, non la propria vista privilegiata (AC10, AC10b).

          `viewAs="public"` spegne i poteri del proprietario — raccomandazioni
          date, inviti pendenti, revoca — e nient'altro. **Non** governa
          l'identità dei raccomandanti: questa scheda è l'unico punto di
          montaggio fuori dalla tab del proprietario (pagina pubblica, sheet
          della ricerca attiva, talent pool), quindi legarci quel predicato lo
          renderebbe sempre falso e la foto non comparirebbe a nessuno. Chi
          decide è la sessione di chi guarda (AC5b, emendata il 2026-09-10).
        */}
        <RecommendationsSection
          profileId={profileId()}
          hideWhenEmpty
          viewAs="public"
        />

        {/*
          Esperienze — sezione **intera** dietro la condizione, intestazione
          compresa.

          Prima l'intestazione era sempre presente e il corpo cadeva su un
          fallback ("No professional experience listed", per giunta in inglese):
          su un profilo giovane la pagina pubblica si riempiva di titoli orfani
          che raccontavano ciò che la persona *non* ha. D11/AC8d: una sezione
          vuota non si annuncia, si omette.

          Un guasto però **non è** un'assenza: `isError` ha il proprio ramo con
          "Riprova", così un errore di rete non viene letto come "questa persona
          non ha esperienze" (AC4c).
        */}
        <Show when={jobsQuery.isError}>
          <div class="space-y-2 border-b pb-4 lg:pb-6">
            <div class="flex items-center gap-2">
              <BiSolidBriefcase size={18} class="text-gray-700" />
              <h2 class="text-base font-medium">
                {t("profile.details.profileInfo.experience")}
              </h2>
            </div>
            <SectionError onRetry={() => void jobsQuery.refetch()} />
          </div>
        </Show>
        <Show
          when={jobsQuery.isSuccess && jobsQuery.data?.previous_jobs?.length}
        >
          <div class="space-y-2 border-b pb-4 lg:pb-6">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <BiSolidBriefcase size={18} class="text-gray-700" />
                <h2 class="text-base font-medium">
                  {t("profile.details.profileInfo.experience")}
                </h2>
                <Badge variant="secondary" size="sm" class="rounded-full">
                  {jobsQuery.data?.previous_jobs?.length ?? 0}
                </Badge>
              </div>
            </div>
            <div class="space-y-4 mt-4">
              <For each={jobsQuery.data?.previous_jobs}>
                {(job) => (
                  <Card class="shadow-none">
                    <CardHeader>
                      <CardTitle>{job.job_title}</CardTitle>
                      <CardDescription>{job.company_name}</CardDescription>
                      <div class="flex items-center gap-2 text-sm text-gray-500 mb-2">
                        <BiSolidCalendar size={16} />
                        <span>
                          {formatMonthYearReadable(job.start_date)}
                          {job.is_current ? (
                            <span class="ml-2">
                              - {t("profile.details.profileInfo.present")}
                            </span>
                          ) : job.end_date ? (
                            ` - ${formatMonthYearReadable(job.end_date)}`
                          ) : null}
                        </span>

                        <Show when={job.is_current}>
                          <Badge
                            variant="light-green"
                            size="sm"
                            class="rounded-full hidden md:block"
                          >
                            {t("profile.details.profileInfo.current_position")}
                          </Badge>
                        </Show>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Show when={job.description}>
                        <p class="text-sm text-gray-600 leading-relaxed hidden md:block">
                          {job.description}
                        </p>
                      </Show>
                    </CardContent>
                  </Card>
                )}
              </For>
            </div>
          </div>
        </Show>

        {/* Istruzione — stesse regole della sezione esperienze. */}
        <Show when={educationQuery.isError}>
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <BiSolidGraduation size={18} class="text-gray-700" />
              <h2 class="text-base font-medium">
                {t("profile.details.profileInfo.education")}
              </h2>
            </div>
            <SectionError onRetry={() => void educationQuery.refetch()} />
          </div>
        </Show>
        <Show
          when={
            educationQuery.isSuccess && educationQuery.data?.education.length
          }
        >
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <BiSolidGraduation size={18} class="text-gray-700" />
              <h2 class="text-base font-medium">
                {t("profile.details.profileInfo.education")}
              </h2>
            </div>
            <div class="space-y-4 mt-4">
              <For each={educationQuery.data?.education}>
                {(education) => (
                  <Card class="shadow-none">
                    <CardContent class="py-6">
                      <div class="flex items-start gap-3">
                        <div class="flex-1">
                          <h3 class="text-sm font-semibold text-gray-900">
                            {education.institution}
                          </h3>
                          <p class="text-sm text-gray-600">
                            {education.degree_type}
                            {education.field_of_study &&
                              ` - ${education.field_of_study}`}
                          </p>
                          <Show
                            when={education.start_date || education.end_date}
                            fallback={null}
                          >
                            <p class="text-sm text-gray-500">
                              {education.start_date &&
                                formatMonthYearReadable(education.start_date)}
                              {education.end_date || education.is_current
                                ? ` - ${
                                    education.is_current
                                      ? t("profile.details.profileInfo.present")
                                      : formatMonthYearReadable(
                                          education.end_date!,
                                        )
                                  }`
                                : null}
                            </p>
                          </Show>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
};
